/**
 * Meldungs-Detail (F19): Felder, Screenshots, Unterhaltung + Aktionen (F21/F22).
 *
 * Seit #108 (Hans 28.09.2026) ist das auch die ERFASSUNG: Ein eigener Entwurf
 * wird hier bearbeitet — dieselbe Darstellung, die später die Meldung ist,
 * nur im Erfassungsmodus: Titel und Typ im Kopf, Ist/Soll und
 * Akzeptanzkriterien editierbar, der KI-Dialog in der Unterhaltung,
 * Screenshots hinzufügen/bearbeiten, Absenden. Der Melder lernt eine
 * Oberfläche, nicht zwei. Styling nach Design-Handoff (Screen 04).
 */

import { useEffect, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Building2,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  Images,
  List,
  MapPin,
  MessagesSquare,
  Pencil,
  Sparkles,
  Trash2,
  Triangle,
  Upload,
  User,
} from "lucide-react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useKunden } from "@/lib/kunden"
import { reihenfolgeNachbarn } from "@/lib/meldungsReihenfolge"
import { toast } from "sonner"
import { AnnotationEditor } from "@/components/erfassung/AnnotationEditor"
import { ErfassungsChat } from "@/components/erfassung/ErfassungsChat"
import { ScreenshotBild } from "@/components/erfassung/ScreenshotBild"
import { ScreenshotLeuchtkasten } from "@/components/erfassung/ScreenshotLeuchtkasten"
import { Unterhaltung } from "@/components/meldung/Unterhaltung"
import { PraezisierungsAssistent } from "@/components/review/PraezisierungsAssistent"
import { ReviewAktionen } from "@/components/review/ReviewAktionen"
import { PRIO_LABEL, STATUS_FARBE, TYP_LABEL, statusMitZug } from "@/lib/labels"
import { dateiAlsBlob, sitzungsCaptureFalls, tabVerbindenUndAufnehmen } from "@/lib/screenshot"
import { api, apiError } from "@/services/api"
import { useAuthStore } from "@/store/auth"
import { StatusKette } from "@/components/meldung/StatusKette"
import { istNinoxAnwendung } from "@/components/AppSwitcher"
import type { Annotation, ChatNachricht, Prioritaet, ReportKontext, ReportTyp } from "@/types"

/** Sektions-Karte + Uppercase-Überschrift (Design Screen 04) */
export const SEKTION = "rounded-xl border bg-card p-5 shadow-card"
export const SEKTION_TITEL =
  "mb-2.5 text-[13px] font-bold uppercase tracking-wide text-foreground/70"
const FELD = "w-full rounded-lg border bg-background px-3 py-2 text-sm leading-relaxed"

export function MeldungDetailPage() {
  const { id } = useParams<{ id: string }>()
  const reportId = Number(id)
  const navigate = useNavigate()
  // Nachbarn in der zuletzt angezeigten Liste (Hans 14.10.2026)
  const [vorige, naechste] = reihenfolgeNachbarn(reportId)
  // Kundenname (Organisation der Anwendung) für Kopf und Schwebe-Element
  const { kundeVonAnwendung } = useKunden()
  const queryClient = useQueryClient()
  const istKundenadmin = useAuthStore((s) => s.isKundenadmin())
  const istReviewer = useAuthStore((s) => s.isReviewer())
  const benutzerId = useAuthStore((s) => s.user?.id)
  const [verwerfenOffen, setVerwerfenOffen] = useState(false)
  const [begruendung, setBegruendung] = useState("")
  // Leuchtkasten: Index des großgezeigten Screenshots, null = zu
  const [leuchtkasten, setLeuchtkasten] = useState<number | null>(null)
  const dateiInputRef = useRef<HTMLInputElement>(null)

  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
  })
  const { data: report, error } = useQuery({
    queryKey: ["reports", "detail", reportId],
    queryFn: () => api.reports.get(reportId),
    enabled: Number.isFinite(reportId),
  })

  const invalidieren = () => {
    void queryClient.invalidateQueries({ queryKey: ["reports"] })
  }

  // ---- Erfassungsmodus (#108): eigener Entwurf ----------------------------
  const erfassung = !!report && report.status === "entwurf" && report.melderSub === benutzerId

  const [titel, setTitel] = useState("")
  const [typ, setTyp] = useState<ReportTyp | null>(null)
  const [ist, setIst] = useState("")
  const [soll, setSoll] = useState("")
  const [akText, setAkText] = useState("")
  const [chatNachrichten, setChatNachrichten] = useState<ChatNachricht[]>([])
  // Formular nur EINMAL je Meldung aus dem Server-Stand füllen — danach ist
  // der Melder die Quelle (Refetches dürfen seine Eingaben nicht überschreiben)
  const formGeladenFuer = useRef<number | null>(null)
  useEffect(() => {
    if (!report || formGeladenFuer.current === report.id) return
    formGeladenFuer.current = report.id
    setTitel(report.titel)
    setTyp(report.typ)
    setIst(report.istBeschreibung ?? "")
    setSoll(report.sollBeschreibung ?? "")
    setAkText((report.akzeptanzkriterien ?? []).join("\n"))
    setChatNachrichten(report.chat)
  }, [report])

  const akzeptanzkriterien = akText
    .split("\n")
    .map((z) => z.trim())
    .filter(Boolean)

  /** Manuelle Eingaben sichern — beim Verlassen eines Feldes und vor jeder Aktion. */
  const felderSpeichern = async () => {
    await api.reports.update(reportId, {
      titel: titel || undefined,
      typ: typ ?? undefined,
      istBeschreibung: ist || null,
      sollBeschreibung: soll || null,
      akzeptanzkriterien,
    })
  }
  const speichernStill = () => {
    if (!erfassung) return
    felderSpeichern().catch((e) => toast.error(apiError(e)))
  }

  const kiZusammenfassen = useMutation({
    mutationFn: async () => {
      await felderSpeichern()
      return api.reports.finalize(reportId)
    },
    onSuccess: (r) => {
      setTitel(r.titel)
      setTyp(r.typ)
      setIst(r.istBeschreibung ?? "")
      setSoll(r.sollBeschreibung ?? "")
      setAkText((r.akzeptanzkriterien ?? []).join("\n"))
      invalidieren()
      toast.success("KI-Zusammenfassung übernommen — bitte prüfen und absenden")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const absenden = useMutation({
    mutationFn: async () => {
      await felderSpeichern()
      if (report) await api.reports.update(reportId, { prioritaet: report.prioritaet })
      return api.reports.absenden(reportId)
    },
    onSuccess: () => {
      invalidieren()
      toast.success("Meldung abgesendet")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // Hans 14.10.2026: Review-Schritte „Gesichtet" und „Freigeben" auch im
  // schwebenden Rand — dieselben Aufrufe wie in ReviewAktionen.
  const randSichten = useMutation({
    mutationFn: () => api.reports.review(reportId, { aktion: "gesichtet" }),
    onSuccess: () => {
      invalidieren()
      toast.success("Als gesichtet markiert")
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const randFreigeben = useMutation({
    mutationFn: () => api.reports.freigeben(reportId),
    onSuccess: (r) => {
      invalidieren()
      toast.success(`Freigegeben — Forgejo-Issue #${r.forgejoIssueNr}`)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const entwurfLoeschen = useMutation({
    mutationFn: () => api.reports.remove(reportId),
    onSuccess: () => {
      invalidieren()
      toast.success("Entwurf gelöscht")
      navigate("/")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // ---- Screenshots: hinzufügen (immer), bearbeiten/entfernen (Entwurf) ----
  // Hans 06.10.2026: Im Entwurf nach einem neuen Screenshot direkt zur
  // Eingabezeile des Erfassungs-Assistenten — ohne Scrollen. Der Anker hält,
  // während das Bild nachlädt und die Seite wächst; eigene Bewegung
  // (Rad, Touch, Klick) oder 4 s beenden ihn.
  const eingabeRef = useRef<HTMLInputElement>(null)
  const ankerBeenden = useRef<(() => void) | null>(null)
  const zurEingabe = () => {
    ankerBeenden.current?.()
    const feld = eingabeRef.current
    if (!feld) return
    const ausrichten = () => feld.scrollIntoView({ block: "end", behavior: "instant" })
    ausrichten()
    feld.focus({ preventScroll: true })
    const ro = new ResizeObserver(ausrichten)
    ro.observe(document.body)
    const ereignisse = ["wheel", "touchstart", "mousedown"] as const
    const beenden = () => {
      ro.disconnect()
      ereignisse.forEach((e) => window.removeEventListener(e, beenden))
      window.clearTimeout(zeit)
      ankerBeenden.current = null
    }
    ereignisse.forEach((e) => window.addEventListener(e, beenden, { passive: true }))
    const zeit = window.setTimeout(beenden, 4000)
    ankerBeenden.current = beenden
  }
  useEffect(() => () => ankerBeenden.current?.(), [])

  // Erste Nachricht: der Assistent wächst auf volle Höhe — die Eingabe
  // (mit Fokus) dabei im Bild halten
  const vorherigeAnzahl = useRef(chatNachrichten.length)
  useEffect(() => {
    const vorher = vorherigeAnzahl.current
    vorherigeAnzahl.current = chatNachrichten.length
    if (vorher === 0 && chatNachrichten.length > 0 && document.activeElement === eingabeRef.current) {
      requestAnimationFrame(() =>
        eingabeRef.current?.scrollIntoView({ block: "nearest", behavior: "instant" })
      )
    }
  }, [chatNachrichten.length])

  const hochladen = useMutation({
    mutationFn: (bild: Blob) => api.reports.screenshots.upload(reportId, bild, []),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] })
      toast.success("Screenshot hinzugefügt")
      if (erfassung) zurEingabe()
    },
    onError: (e) => toast.error(apiError(e)),
  })
  // Hans 28.09.: Verbindung auch hier herstellbar (nach Neuladen fehlt sie).
  const captureSchluessel = report ? `app:${report.applicationId}` : null
  const [tabVerbunden, setTabVerbunden] = useState(false)
  useEffect(() => {
    setTabVerbunden(captureSchluessel !== null && sitzungsCaptureFalls(captureSchluessel) !== null)
  }, [captureSchluessel])
  const vomTabAufnehmen = async () => {
    if (!captureSchluessel) return
    try {
      const bild = await tabVerbindenUndAufnehmen(
        captureSchluessel,
        !!report?.kontext?.ninoxTeamId
      )
      setTabVerbunden(true)
      hochladen.mutate(bild)
    } catch (e) {
      setTabVerbunden(sitzungsCaptureFalls(captureSchluessel) !== null)
      if ((e as Error).name !== "NotAllowedError") {
        toast.error("Aufnahme nicht möglich — bitte den Anwendungs-Tab erneut freigeben")
      }
    }
  }

  const [bearbeitung, setBearbeitung] = useState<{ blob: Blob; altId: number } | null>(null)
  const bearbeitenOeffnen = async (screenshotId: number) => {
    try {
      const blob = await api.reports.screenshots.datei(reportId, screenshotId)
      setBearbeitung({ blob, altId: screenshotId })
    } catch (e) {
      toast.error(apiError(e))
    }
  }
  const bearbeitungFertig = useMutation({
    mutationFn: async ({
      bild,
      annotationen,
      altId,
    }: {
      bild: Blob
      annotationen: Annotation[]
      altId: number
    }) => {
      await api.reports.screenshots.upload(reportId, bild, annotationen)
      await api.reports.screenshots.remove(reportId, altId)
    },
    onSuccess: () => {
      setBearbeitung(null)
      invalidieren()
      toast.success("Screenshot aktualisiert")
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const entfernen = useMutation({
    mutationFn: (screenshotId: number) => api.reports.screenshots.remove(reportId, screenshotId),
    onSuccess: () => invalidieren(),
    onError: (e) => toast.error(apiError(e)),
  })

  // ---- Kundenadmin / Priorität (F21/F22) -----------------------------------
  const prioAendern = useMutation({
    mutationFn: (prioritaet: Prioritaet) => api.reports.update(reportId, { prioritaet }),
    onSuccess: () => {
      invalidieren()
      toast.success("Priorität geändert")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const verwerfen = useMutation({
    mutationFn: () => api.reports.verwerfen(reportId, begruendung.trim() || null),
    onSuccess: () => {
      invalidieren()
      setVerwerfenOffen(false)
      toast.success("Meldung verworfen")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  if (error != null) {
    return (
      <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">{apiError(error)}</p>
    )
  }
  if (!report) return <p className="text-muted-foreground">Lade Meldung …</p>

  const kontext = report.kontext ?? {}
  const meineAnwendung = anwendungen?.find((a) => a.id === report.applicationId)
  const kunde = kundeVonAnwendung(report.applicationId)
  const prioAenderbar = istKundenadmin || erfassung
  // testerwerk#57: von netz-fabrik aus einem bestehenden Issue übernommen
  const uebernommen = (report.kontext as ReportKontext | null)?.uebernommen
  const screenshotsErgaenzbar =
    erfassung || (report.status !== "entwurf" && !["abgelehnt", "verworfen"].includes(report.status))

  // Was fehlt noch zum Absenden? (Screenshot ist Pflicht — #108)
  const fehlt: string[] = []
  if (erfassung) {
    if (!typ) fehlt.push("Fehler oder Änderungswunsch wählen")
    if (!titel.trim()) fehlt.push("Titel")
    if (!ist.trim() && !soll.trim()) fehlt.push("Beschreibung (Ist oder Soll)")
    if (report.screenshots.length === 0) fehlt.push("mindestens ein Screenshot")
  }

  // ---- Bausteine — Reihenfolge je Modus (Hans 28.09.): im Entwurf folgt
  // sie dem Arbeitsfluss (Screenshots → Assistent → Ist/Soll → …), im
  // Lesemodus wandert die Unterhaltung hinter Ist/Soll/Akzeptanzkriterien.
  const typAktuell = erfassung ? typ : report.typ

  // Hans 06.10.2026: schmale Leiste (eine Zeile) — die Anleitung steht im
  // Tooltip, sichtbar bleiben nur Status, Offenes und die beiden Aktionen.
  const leiste = erfassung && (
    <section
      className="sticky top-2 z-20 flex items-center justify-between gap-3 rounded-lg border border-primary/25 bg-card/95 px-3 py-1.5 shadow-card backdrop-blur"
      title="Screenshots oben, dann kurz mit dem Erfassungs-Assistenten sprechen und unter dem Dialog die KI-Zusammenfassung erstellen lassen. Dann absenden."
    >
      <p className="min-w-0 truncate text-[12.5px]">
        <span className="font-semibold">Entwurf</span>
        <span className="text-muted-foreground"> · nur für dich sichtbar</span>
        {fehlt.length > 0 && (
          <span className="text-warning"> · Noch offen: {fehlt.join(" · ")}</span>
        )}
      </p>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={() => entwurfLoeschen.mutate()}
          disabled={entwurfLoeschen.isPending}
          className="rounded-md bg-secondary px-2.5 py-1 text-[12px] font-semibold hover:bg-secondary/80 disabled:opacity-50"
        >
          Entwurf löschen
        </button>
        <button
          type="button"
          onClick={() => absenden.mutate()}
          disabled={fehlt.length > 0 || absenden.isPending}
          title={fehlt.length > 0 ? `Noch offen: ${fehlt.join(", ")}` : undefined}
          className="rounded-md bg-primary px-3 py-1 text-[12px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          {absenden.isPending ? "Sende …" : "Meldung absenden"}
        </button>
      </div>
    </section>
  )

  const prioritaetWahl = (
    <span className="inline-flex rounded-[10px] bg-secondary p-[3px]">
      {(["niedrig", "mittel", "hoch"] as const).map((p) => (
        <button
          key={p}
          type="button"
          disabled={!prioAenderbar || prioAendern.isPending}
          onClick={() => p !== report.prioritaet && prioAendern.mutate(p)}
          className={`rounded-lg px-3 py-1 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed ${
            report.prioritaet === p
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground enabled:hover:text-foreground disabled:opacity-70"
          }`}
        >
          {PRIO_LABEL[p]}
        </button>
      ))}
    </span>
  )

  // Hans 28.09.: Bilder, die noch auf ihre Nachricht warten, zeigt die
  // Unterhaltung — nicht die Galerie.
  const galerie = report.screenshots.filter((s) => !s.zuNachricht)
  const screenshotsBlock = (galerie.length > 0 || screenshotsErgaenzbar) && (
    <section className={SEKTION}>
      <h2 className={SEKTION_TITEL}>Screenshots ({galerie.length})</h2>
      {/* Volle Anzeigebreite (Wunsch 12.08.2026): halbe Spalten machten Text in
          den Screenshots unlesbar — der Klick-Zoom bleibt für die Details. */}
      <div className="grid grid-cols-1 gap-3">
        {galerie.map((s, i) => (
          <div key={s.id} className="relative">
            <button
              type="button"
              onClick={() => setLeuchtkasten(i)}
              title="Klicken zum Vergrößern"
              className="block w-full cursor-zoom-in rounded-md transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            >
              <ScreenshotBild reportId={report.id} screenshot={s} />
            </button>
            {erfassung && (
              <div className="absolute right-2 top-2 flex gap-1.5">
                <button
                  type="button"
                  onClick={() => void bearbeitenOeffnen(s.id)}
                  className="flex items-center gap-1 rounded bg-primary px-2 py-1 text-[11px] font-bold text-primary-foreground hover:bg-primary-hover"
                >
                  <Pencil className="h-3 w-3" /> Markieren / Schwärzen
                </button>
                <button
                  type="button"
                  onClick={() => entfernen.mutate(s.id)}
                  className="flex items-center gap-1 rounded bg-destructive px-2 py-1 text-[11px] font-bold text-destructive-foreground"
                >
                  <Trash2 className="h-3 w-3" /> Entfernen
                </button>
              </div>
            )}
          </div>
        ))}
        {galerie.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Noch kein Screenshot — mindestens einer gehört zur Meldung.
          </p>
        )}
      </div>
      {screenshotsErgaenzbar && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
          <span className="text-[13px] font-semibold">Screenshot hinzufügen:</span>
          <button
            type="button"
            onClick={() => void vomTabAufnehmen()}
            disabled={hochladen.isPending}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            <Camera className="h-3.5 w-3.5" />{" "}
            {tabVerbunden ? "Vom verbundenen Tab" : "Tab verbinden & Screenshot"}
          </button>
          <button
            type="button"
            onClick={() => dateiInputRef.current?.click()}
            disabled={hochladen.isPending}
            className="flex items-center gap-1.5 rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80 disabled:opacity-50"
          >
            <Upload className="h-3.5 w-3.5" /> Bild hochladen
          </button>
          {!tabVerbunden && (
            <span className="text-xs text-muted-foreground">
              Der Browser fragt einmal, welcher Tab geteilt wird — die Verbindung bleibt
              für diese Sitzung.
            </span>
          )}
          {!erfassung && (
            <span className="text-xs text-muted-foreground">
              Nachgereichte Bilder gehen als Nachricht an die andere Seite.
            </span>
          )}
          <input
            ref={dateiInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void dateiAlsBlob(file).then((b) => hochladen.mutate(b))
              e.target.value = ""
            }}
          />
        </div>
      )}
    </section>
  )

  const unterhaltungBlock = erfassung ? (
    <section className={SEKTION}>
      <h2 className={SEKTION_TITEL}>Erfassungs-Assistent</h2>
      {/* Hans 06.10.2026: Solange noch nichts geschrieben ist, bleibt der
          Assistent kompakt — Einleitung und direkt darunter die Eingabe, so
          sind Screenshot und Eingabe zugleich im Bild. Ab der ersten
          Nachricht volle Höhe, die Eingabe (dasselbe Feld) rutscht nach unten. */}
      <div className={`flex flex-col ${chatNachrichten.length > 0 ? "h-[460px]" : ""}`}>
        <ErfassungsChat
          reportId={report.id}
          nachrichten={chatNachrichten}
          onNeueNachrichten={setChatNachrichten}
          hoch
          eingabeRef={eingabeRef}
        />
      </div>
      {/* Abschluss des Dialogs (Hans 28.09.): die Zusammenfassung gehört hierher,
          nicht in die Kopfleiste — sie macht aus dem Gespräch die Meldung. */}
      <div className="mt-3 flex flex-wrap items-center gap-3 border-t pt-3">
        <button
          type="button"
          onClick={() => kiZusammenfassen.mutate()}
          disabled={
            kiZusammenfassen.isPending || (chatNachrichten.length === 0 && !titel && !ist && !soll)
          }
          title="Strukturiert Dialog + Screenshots zum Mini-PRD (F14)"
          className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-bold disabled:opacity-50 ${
            chatNachrichten.length > 0
              ? "bg-primary text-primary-foreground hover:bg-primary-hover"
              : "bg-secondary hover:bg-secondary/80"
          }`}
        >
          <Sparkles className="h-3.5 w-3.5" />
          {kiZusammenfassen.isPending ? "KI strukturiert …" : "KI-Zusammenfassung erstellen"}
        </button>
        <span className="text-xs text-muted-foreground">
          Macht aus dem Gespräch die Meldung: füllt Titel, Ist/Soll und Akzeptanzkriterien
          — unten nachbessern, dann absenden.
        </span>
      </div>
    </section>
  ) : (
    <Unterhaltung report={report} istReviewer={istReviewer} />
  )

  const istSollBlock = (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <section className={SEKTION}>
        <h2 className={SEKTION_TITEL}>
          {typAktuell === "aenderungswunsch" ? "Heutiger Zustand / Workaround" : "Ist-Verhalten"}
        </h2>
        {erfassung ? (
          <textarea
            value={ist}
            onChange={(e) => setIst(e.target.value)}
            onBlur={speichernStill}
            rows={5}
            placeholder="Was passiert heute? (die KI-Zusammenfassung füllt das aus)"
            className={FELD}
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {report.istBeschreibung || "—"}
          </p>
        )}
      </section>
      <section className={SEKTION}>
        <h2 className={SEKTION_TITEL}>
          {typAktuell === "aenderungswunsch" ? "Gewünschtes Verhalten" : "Soll-Verhalten"}
        </h2>
        {erfassung ? (
          <textarea
            value={soll}
            onChange={(e) => setSoll(e.target.value)}
            onBlur={speichernStill}
            rows={5}
            placeholder="Was soll stattdessen passieren?"
            className={FELD}
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {report.sollBeschreibung || "—"}
          </p>
        )}
      </section>
    </div>
  )

  const akBlock = (erfassung || (report.akzeptanzkriterien && report.akzeptanzkriterien.length > 0)) && (
    <section className={SEKTION}>
      <h2 className={SEKTION_TITEL}>Akzeptanzkriterien</h2>
      {erfassung ? (
        <textarea
          value={akText}
          onChange={(e) => setAkText(e.target.value)}
          onBlur={speichernStill}
          rows={4}
          placeholder="Ein Kriterium je Zeile — woran erkennt man, dass es erledigt ist? (die KI-Zusammenfassung schlägt sie vor)"
          className={FELD}
        />
      ) : (
        <ul className="list-disc pl-5 text-sm leading-7">
          {report.akzeptanzkriterien!.map((k, i) => (
            <li key={i}>{k}</li>
          ))}
        </ul>
      )}
    </section>
  )

  // Struktur-Bezug (nach nxtools-schemas#18): beteiligte Tabellen/Felder nach
  // Stand der Datenbank, die Lücke und der gewünschte Ablauf — kommt aus der
  // KI-Zusammenfassung, nur Anzeige
  const bezug = report.strukturBezug
  const bezugBlock = bezug &&
    (bezug.beteiligteStellen.length > 0 || bezug.luecke || bezug.ablauf.length > 0) && (
      <section className={SEKTION}>
        <h2 className={SEKTION_TITEL}>Beteiligte Stellen</h2>
        <p className="mb-2 text-xs text-muted-foreground">
          Nach dem Stand der Datenbank beim Melden{bezug.datenbank ? ` — Datenbank „${bezug.datenbank}“` : ""}
        </p>
        {bezug.beteiligteStellen.length > 0 && (
          <ul className="list-disc pl-5 text-sm leading-7">
            {bezug.beteiligteStellen.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        )}
        {bezug.luecke && (
          <p className="mt-2 text-sm leading-7">
            <span className="font-semibold">Lücke heute:</span> {bezug.luecke}
          </p>
        )}
        {bezug.ablauf.length > 0 && (
          <>
            <h3 className="mt-3 text-sm font-semibold">Gewünschter Ablauf</h3>
            <ul className="list-disc pl-5 text-sm leading-7">
              {bezug.ablauf.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </>
        )}
      </section>
    )

  const einordnungBlock = (report.machbarkeit || report.modulId || report.duplikatVon) && (
    <section className={SEKTION}>
      <h2 className={SEKTION_TITEL}>Einordnung</h2>
      <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-sm">
        <dt className="text-muted-foreground">Tabelle</dt>
        <dd className="font-medium">
          {report.modulId ?? "—"}
          {/* F32: Reviewer springt in den Struktur-Browser der Tabelle */}
          {istReviewer && report.modulId?.includes(":") && (
            <Link
              to={`/admin/schema?db=${encodeURIComponent(report.modulId.split(":")[0])}&tabelle=${encodeURIComponent(report.modulId.split(":")[1])}`}
              className="ml-2 font-semibold text-primary underline"
            >
              Im Struktur-Browser öffnen
            </Link>
          )}
        </dd>
        <dt className="text-muted-foreground">Machbarkeit (unverbindlich)</dt>
        <dd className="font-medium">{report.machbarkeit ?? "—"}</dd>
        <dt className="text-muted-foreground">Mögliches Duplikat</dt>
        <dd>
          {report.duplikatVon ? (
            <span>
              <Link to={`/meldungen/${report.duplikatVon}`} className="font-medium text-primary underline">
                Meldung #{report.duplikatVon}
              </Link>
              {erfassung && (
                <span className="ml-2 text-muted-foreground">
                  — du kannst diesen Entwurf löschen und dich der bestehenden Meldung anschließen.
                </span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground/70">—</span>
          )}
        </dd>
      </dl>
    </section>
  )

  // Im Lesemodus bleibt die Priorität ein eigener Abschnitt (Kundenadmin,
  // F22); im Entwurf steht sie im Kopf neben dem Typ.
  const prioritaetBlock = !erfassung && (
    <section className={SEKTION}>
      <h2 className={SEKTION_TITEL}>Priorität (Kundensicht)</h2>
      {prioritaetWahl}
    </section>
  )

  const absendenUnten = erfassung && (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={() => absenden.mutate()}
        disabled={fehlt.length > 0 || absenden.isPending}
        title={fehlt.length > 0 ? `Noch offen: ${fehlt.join(", ")}` : undefined}
        className="rounded-lg bg-primary px-5 py-2.5 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
      >
        {absenden.isPending ? "Sende …" : "Meldung absenden"}
      </button>
    </div>
  )

  // Hans 02.10.2026: Der Kontext richtet sich nach der Anwendungsart — eine
  // Web-Anwendung hat keine Ninox-Tabelle/-Team/-Sicht; dort zählen Adresse,
  // Seite und Version.
  const istNinox = Boolean(kontext.ninoxTeamId) || (meineAnwendung ? istNinoxAnwendung(meineAnwendung) : false)
  const umgebungsZeile = (
    <>
      <dt className="text-muted-foreground">Umgebung</dt>
      <dd>
        {kontext.umgebung === "live" ? (
          <span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-bold text-warning">
            Live (echte Daten)
          </span>
        ) : kontext.umgebung === "dev" ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">
            {istNinox ? "DEV (Testdaten)" : "Testumgebung"}
          </span>
        ) : (
          "—"
        )}
      </dd>
    </>
  )
  const zeitBrowser = (
    <>
      <dt className="text-muted-foreground">Zeitpunkt</dt>
      <dd>
        {kontext.zeitpunkt ? new Date(String(kontext.zeitpunkt)).toLocaleString("de-DE") : "—"}
      </dd>
      <dt className="text-muted-foreground">Browser</dt>
      <dd className="break-all font-mono text-xs">{String(kontext.browser ?? "—")}</dd>
    </>
  )
  const kontextBlock = istNinox ? (
    <section className={SEKTION}>
      <h2 className={SEKTION_TITEL}>Ninox-Stelle & Kontext (F8)</h2>
      <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-[13px]">
        <dt className="text-muted-foreground">Tabelle</dt>
        <dd>
          {String(kontext.tabelleName ?? "—")}
          {kontext.tabelleId ? (
            <span className="ml-1.5 font-mono text-xs text-muted-foreground">
              ({String(kontext.tabelleId)})
            </span>
          ) : null}
        </dd>
        <dt className="text-muted-foreground">Ninox-Team</dt>
        <dd className="font-mono">
          {String(kontext.ninoxTeamId ?? meineAnwendung?.ninoxTeamId ?? "—")}
        </dd>
        <dt className="text-muted-foreground">Datenbank</dt>
        <dd className="font-mono">{String(kontext.ninoxDatabaseId ?? "—")}</dd>
        <dt className="text-muted-foreground">Sicht</dt>
        <dd className="font-mono">{String(kontext.viewId ?? "—")}</dd>
        {umgebungsZeile}
        <dt className="text-muted-foreground">Ninox-Link</dt>
        <dd className="break-all font-mono text-xs">
          {String(kontext.ninoxUrl ?? kontext.applicationUrl ?? "—")}
        </dd>
        {zeitBrowser}
      </dl>
    </section>
  ) : (
    <section className={SEKTION}>
      <h2 className={SEKTION_TITEL}>Kontext (F8)</h2>
      <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-[13px]">
        <dt className="text-muted-foreground">Anwendung</dt>
        <dd>{meineAnwendung?.name ?? "—"}</dd>
        {umgebungsZeile}
        <dt className="text-muted-foreground">Adresse</dt>
        <dd className="break-all font-mono text-xs">
          {kontext.applicationUrl ? (
            <a
              href={String(kontext.applicationUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              {String(kontext.applicationUrl)}
            </a>
          ) : (
            "—"
          )}
        </dd>
        <dt className="text-muted-foreground">Seite</dt>
        <dd className="break-all font-mono text-xs">{String(kontext.route ?? "—")}</dd>
        <dt className="text-muted-foreground">App-Version</dt>
        <dd className="font-mono text-xs">{String(kontext.appVersion ?? "—")}</dd>
        {zeitBrowser}
      </dl>
    </section>
  )

  return (
    <div className="mx-auto flex max-w-[1240px] flex-col gap-4">
      {/* Hans 06.10.2026: Issue-Nummer groß im rechten Rand, bleibt beim
          Scrollen stehen; Link zum Forgejo-Issue nur für Reviewer.
          Hans 14.10.2026: dazu die Navigation — vorige / nächste Meldung in
          der zuletzt angezeigten Listen-Reihenfolge und zurück zur Liste —
          und das Ganze schwebt ab 1520 px Fensterbreite vertikal MITTIG im
          rechten Seitenrand (fixed, außerhalb des Inhaltsrahmens). Schmaler
          reicht der Rand nicht: dann wie bisher oben rechts im Rahmen
          (sticky, Höhe 0 — nimmt keinen Platz im Fluss ein). */}
      {!erfassung && (
        <div className="pointer-events-none sticky top-4 z-30 -mb-4 flex h-0 justify-end">
          {/* Hans 14.10.2026: Dreiecke über und unter dem Kasten — Seitenanfang
              bzw. Seitenende (letzter Eintrag). Der Rahmen trägt die Position,
              der Kasten darin bleibt unverändert. */}
          <div className="pointer-events-auto flex h-fit w-44 flex-col items-center gap-1 min-[1640px]:fixed min-[1640px]:left-[calc(50%+620px+1.25rem)] min-[1640px]:top-1/2 min-[1640px]:-translate-y-1/2">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            title="Zum Seitenanfang"
            className="rounded-md p-1 text-primary/70 hover:bg-primary/10 hover:text-primary"
          >
            <Triangle className="h-4 w-4 fill-current" />
          </button>
          <div className="flex w-full flex-col items-stretch gap-1.5 rounded-xl border border-primary/30 bg-card/95 p-1.5 shadow-card backdrop-blur sm:p-2">
            {kunde && (
              <p
                className="flex items-center gap-1 truncate px-1.5 text-[11px] font-bold uppercase tracking-wide text-primary"
                title={kunde}
              >
                <Building2 className="h-3 w-3 shrink-0" />
                <span className="truncate">{kunde}</span>
              </p>
            )}
            <p
              className="line-clamp-2 px-1.5 text-[12px] font-semibold leading-snug text-foreground"
              title={report.titel}
            >
              {report.titel}
            </p>
            {/* Melder (Hans 14.10.2026) */}
            <p
              className="flex items-center gap-1 truncate px-1.5 text-[11px] text-muted-foreground"
              title={`Gemeldet von ${report.melderName || report.melderSub}`}
            >
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{report.melderName || report.melderSub}</span>
            </p>
            {(() => {
              const inhalt =
                report.forgejoIssueNr != null ? (
                  <>
                    <span className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
                      Issue
                    </span>
                    <span className="text-[20px] font-extrabold leading-none text-primary sm:text-[28px]">
                      #{report.forgejoIssueNr}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
                      Meldung
                    </span>
                    <span className="text-[20px] font-extrabold leading-none text-muted-foreground sm:text-[24px]">
                      #{report.id}
                    </span>
                  </>
                )
              const kasten = "flex flex-col items-end px-1.5 py-0.5"
              return istReviewer && report.forgejoIssueUrl ? (
                <a
                  href={report.forgejoIssueUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={`Forgejo-Issue #${report.forgejoIssueNr} öffnen`}
                  className={`${kasten} rounded-lg transition-colors hover:bg-primary/10`}
                >
                  {inhalt}
                </a>
              ) : (
                <div className={kasten}>{inhalt}</div>
              )
            })()}
            {istReviewer && report.status === "neu" && (
              <button
                type="button"
                onClick={() => randSichten.mutate()}
                disabled={randSichten.isPending}
                title="Als gesichtet markieren"
                className="flex items-center justify-center gap-1.5 rounded-lg bg-primary px-2 py-1.5 text-[12.5px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                <Eye className="h-4 w-4" />
                Gesichtet
              </button>
            )}
            {istReviewer && report.status === "gesichtet" && (
              <button
                type="button"
                onClick={() => randFreigeben.mutate()}
                disabled={randFreigeben.isPending}
                title="Für die Umsetzung freigeben — erzeugt das Forgejo-Issue"
                className="flex items-center justify-center gap-1.5 rounded-lg bg-success px-2 py-1.5 text-[12.5px] font-bold text-success-foreground hover:opacity-90 disabled:opacity-50"
              >
                <CheckCircle2 className="h-4 w-4" />
                {randFreigeben.isPending ? "Erzeuge Issue …" : "Freigeben"}
              </button>
            )}
            {/* Sprungmarken (Hans 14.10.2026): Screenshots › Ist-Verhalten ›
                Unterhaltung › Stelle & Kontext — in der Reihenfolge der Seite */}
            <div className="flex flex-col gap-0.5 border-t border-primary/15 pt-1.5">
              {(
                [
                  ["abschnitt-screenshots", "Screenshots", Images, Boolean(screenshotsBlock)],
                  ["abschnitt-ist", "Ist-Verhalten", FileText, true],
                  ["abschnitt-unterhaltung", "Unterhaltung", MessagesSquare, true],
                  ["abschnitt-kontext", istNinox ? "Stelle & Kontext" : "Kontext", MapPin, true],
                ] as const
              ).map(([ziel, label, Icon, da]) => (
                <button
                  key={ziel}
                  type="button"
                  disabled={!da}
                  onClick={() =>
                    document.getElementById(ziel)?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                  title={da ? `Zu „${label}“ springen` : `Keine ${label} vorhanden`}
                  className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[11.5px] font-semibold text-foreground/80 hover:bg-primary/10 hover:text-primary disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-end gap-1 border-t border-primary/15 pt-1.5">
              <button
                type="button"
                onClick={() => vorige != null && navigate(`/meldungen/${vorige}`)}
                disabled={vorige == null}
                title={vorige != null ? `Vorige Meldung (#${vorige})` : "Keine vorige Meldung in der Liste"}
                className="rounded-md p-1.5 text-primary hover:bg-primary/10 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronUp className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => naechste != null && navigate(`/meldungen/${naechste}`)}
                disabled={naechste == null}
                title={naechste != null ? `Nächste Meldung (#${naechste})` : "Keine nächste Meldung in der Liste"}
                className="rounded-md p-1.5 text-primary hover:bg-primary/10 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
              <Link
                to="/"
                title="Zurück zur Liste"
                className="rounded-md p-1.5 text-primary hover:bg-primary/10"
              >
                <List className="h-5 w-5" />
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={() =>
              window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" })
            }
            title="Zum Seitenende (letzter Eintrag)"
            className="rounded-md p-1 text-primary/70 hover:bg-primary/10 hover:text-primary"
          >
            <Triangle className="h-4 w-4 rotate-180 fill-current" />
          </button>
          </div>
        </div>
      )}
      <div className="text-[13px]">
        <Link to="/" className="font-semibold text-primary hover:underline">
          ← Zur Übersicht
        </Link>
      </div>

      <div
        className={`flex items-start justify-between gap-5 ${
          !erfassung ? "pr-48 min-[1640px]:pr-0" : ""
        }`}
      >
        <div className="min-w-0 flex-1">
          {erfassung ? (
            <input
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              onBlur={speichernStill}
              maxLength={255}
              placeholder="Titel der Meldung — kurz und prägnant (die KI-Zusammenfassung schlägt einen vor)"
              className="mb-2 w-full rounded-lg border bg-background px-3 py-2 text-[20px] font-bold"
            />
          ) : (
            <>
              {kunde && (
                <p className="mb-1 flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-primary">
                  <Building2 className="h-4 w-4" />
                  {kunde}
                </p>
              )}
              <h1 className="mb-2 text-[22px] font-bold">
                {report.titel || <em className="text-muted-foreground">ohne Titel</em>}
              </h1>
            </>
          )}
          <p className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_FARBE[report.status]}`}
            >
              {statusMitZug(report)}
            </span>
            {erfassung ? (
              <>
                <span className="inline-flex rounded-[10px] bg-secondary p-[3px]">
                  {(["fehler", "aenderungswunsch"] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        setTyp(t)
                        api.reports.update(reportId, { typ: t }).catch((e) => toast.error(apiError(e)))
                      }}
                      className={`rounded-lg px-3 py-1 text-[12px] font-semibold transition-colors ${
                        typ === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {TYP_LABEL[t]}
                    </button>
                  ))}
                </span>
                <span title="Priorität aus Kundensicht">{prioritaetWahl}</span>
              </>
            ) : (
              <span>{report.typ ? TYP_LABEL[report.typ] : "—"}</span>
            )}
            <span>
              · #{report.id} · gemeldet von {report.melderName || report.melderSub} am{" "}
              {new Date(report.createdAt).toLocaleString("de-DE")}
            </span>
            {/* Link zum Forgejo-Issue nur für Reviewer (Hans 06.10.2026) */}
            {report.forgejoIssueUrl && istReviewer && (
              <a
                href={report.forgejoIssueUrl}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-primary underline"
              >
                Forgejo-Issue #{report.forgejoIssueNr}
              </a>
            )}
            {report.forgejoIssueNr != null && !istReviewer && (
              <span className="font-semibold text-foreground">Issue #{report.forgejoIssueNr}</span>
            )}
          </p>
          {/* testerwerk#122: der vollständige Weg der Meldung */}
          {!erfassung && (report.kette?.length ?? 0) > 1 && (
            <div className="mt-2">
              <StatusKette report={report} />
            </div>
          )}
          {/* testerwerk#57: sichtbar machen, dass der Melder das nicht selbst erfasst hat */}
          {uebernommen && (
            <p className="mt-2 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2 text-[12.5px]">
              {uebernommen.entwicklung ? (
                <>
                  <strong>Von der Entwicklung angelegt</strong> — {uebernommen.von} über{" "}
                  {uebernommen.quelle ?? "Forgejo"},{" "}
                  {new Date(uebernommen.am).toLocaleDateString("de-DE")}. Kein Kundenbenutzer
                  ist als Melder zugeordnet.
                </>
              ) : (
                <>
                  <strong>Von netz-fabrik übernommen</strong>
                  {uebernommen.quelle ? ` aus „${uebernommen.quelle}“` : ""} — {uebernommen.von},{" "}
                  {new Date(uebernommen.am).toLocaleDateString("de-DE")}. Rückfragen und Antworten
                  laufen ab jetzt hier in der Unterhaltung.
                </>
              )}
            </p>
          )}
        </div>
        {istKundenadmin && report.status === "neu" && (
          <button
            type="button"
            onClick={() => setVerwerfenOffen(true)}
            className="whitespace-nowrap rounded-lg border border-destructive/30 bg-card px-4 py-2 text-[13px] font-semibold text-destructive hover:bg-destructive/5"
          >
            Verwerfen
          </button>
        )}
      </div>

      {leiste}

      {istReviewer && report.status !== "entwurf" && <ReviewAktionen report={report} />}

      {/* F13/S8: Präzisierungs-Assistent — nur Reviewer, vor der Freigabe */}
      {istReviewer && (report.status === "neu" || report.status === "gesichtet") && (
        <PraezisierungsAssistent report={report} />
      )}

      {report.begruendungAblehnung && (
        <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Abgelehnt durch netz-fabrik: {report.begruendungAblehnung}
        </p>
      )}

      {report.begruendungVerwerfung && (
        <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Verworfen durch den Kundenadmin: {report.begruendungVerwerfung}
        </p>
      )}

      {erfassung ? (
        <>
          {screenshotsBlock}
          {unterhaltungBlock}
          {istSollBlock}
          {akBlock}
          {bezugBlock}
          {einordnungBlock}
          {absendenUnten}
          {kontextBlock}
        </>
      ) : (
        <>
          {/* Sprungmarken für das Schwebe-Element (Hans 14.10.2026) */}
          {screenshotsBlock && (
            <div id="abschnitt-screenshots" className="scroll-mt-4">
              {screenshotsBlock}
            </div>
          )}
          <div id="abschnitt-ist" className="flex scroll-mt-4 flex-col gap-4">
            {istSollBlock}
            {akBlock}
            {bezugBlock}
            {einordnungBlock}
            {prioritaetBlock}
          </div>
          <div id="abschnitt-unterhaltung" className="scroll-mt-4">
            {unterhaltungBlock}
          </div>
          <div id="abschnitt-kontext" className="scroll-mt-4">
            {kontextBlock}
          </div>
        </>
      )}

      {leuchtkasten !== null && (
        <ScreenshotLeuchtkasten
          reportId={report.id}
          screenshots={galerie}
          index={leuchtkasten}
          onIndexChange={setLeuchtkasten}
          onClose={() => setLeuchtkasten(null)}
        />
      )}

      {/* Markieren/Schwärzen eines gespeicherten Screenshots (Entwurf) */}
      {bearbeitung && (
        <AnnotationEditor
          bild={bearbeitung.blob}
          onAbbrechen={() => setBearbeitung(null)}
          onFertig={(e) => bearbeitungFertig.mutate({ ...e, altId: bearbeitung.altId })}
        />
      )}

      {/* Verwerfen-Dialog (F21) */}
      {verwerfenOffen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-4">
          <div className="w-full max-w-md rounded-[14px] bg-card p-5 shadow-2xl">
            <h2 className="mb-2 font-bold">Meldung verwerfen</h2>
            <p className="mb-3 text-sm text-muted-foreground">
              Die Meldung geht nicht in die Sichtung bei netz-fabrik. Die Begründung ist für
              den Melder sichtbar.
            </p>
            <textarea
              value={begruendung}
              onChange={(e) => setBegruendung(e.target.value)}
              rows={3}
              placeholder="Begründung (optional)"
              className="mb-3 w-full rounded-lg border px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setVerwerfenOffen(false)}
                className="rounded-lg bg-secondary px-4 py-2 text-[13px] font-semibold"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => verwerfen.mutate()}
                disabled={verwerfen.isPending}
                className="rounded-lg bg-destructive px-4 py-2 text-[13px] font-semibold text-destructive-foreground disabled:opacity-50"
              >
                Verwerfen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
